import { getActiveClient } from "./supabase";

/* ────────── MAPPERS ────────── */

function mapProfile(row) {
  if (!row) return null;
  const visibility = row.visibility || "open";
  // When the row comes from the directory view for a private profile the
  // identity/detail columns are null. We use full_name/handle presence as the
  // signal for whether the viewer has full access to this profile.
  const hasFullAccess = !!(row.full_name || row.handle || row.bio || row.positioning);
  return {
    id: row.id,
    name: row.full_name || row.handle || null,
    userType: row.account_type === "PERSON" ? "person" : "workshop",
    positioning: row.positioning || row.bio || "",
    location: row.location || "",
    distanceKm: 0,
    availability: row.availability || "available",
    compatibility: null,          // real Compatibility engine ships later — no fake %
    matchReasons: [],             // no fabricated breakdown either
    reputation: row.reputation || 0,
    verified: !!row.verified,
    responseRate: row.response_rate || 0,
    responseTimeH: row.response_time_h || 24,
    onTimeRate: row.on_time_rate || 0,
    yearsActive: row.years_active || 0,
    projectsCompleted: row.projects_completed || 0,
    profileCompleteness: row.profile_completeness || 0,
    rateFrom: row.rate_from || 0,
    leadTimeDays: row.lead_time_days || 0,
    skills: row.skills || [],
    materials: row.materials || [],
    processes: row.processes || [],
    equipment: row.equipment || [],
    machines: [],
    certifications: row.certifications || [],
    portfolio: [],
    links: [],
    tags: (row.skills || []).slice(0, 3),
    accountType: row.account_type,
    visibility,
    hasFullAccess,
  };
}

function fmtRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const mins = Math.max(1, Math.floor((now - then) / 60000));
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}g`;
}

function fmtBudget(min, max) {
  const fmt = (v) => `€ ${Number(v).toLocaleString("it-IT")}`;
  if (min == null && max == null) return "—";
  if (min != null && max != null && Number(min) !== Number(max)) return `${fmt(min)} – ${fmt(max).replace("€ ", "")}`;
  return fmt(min ?? max);
}

function mapRequest(row, ownerName = "") {
  if (!row) return null;
  const createdMs = row.created_at ? new Date(row.created_at).getTime() : Date.now();
  return {
    id: row.ref_code || row.id,
    dbId: row.id,
    title: row.title,
    titleEn: row.title,
    description: row.description || "",
    client: ownerName || "—",
    clientType: "person",
    category: row.category || "",
    material: row.material || "",
    requiredProcesses: row.required_processes || [],
    requiredMaterials: row.required_materials || [],
    quantity: row.quantity || 0,
    budget: fmtBudget(row.budget_min, row.budget_max),
    budgetNum: Number(row.budget_max ?? row.budget_min ?? 0),
    deadline: row.deadline || "",
    location: row.location || "",
    distanceKm: 0,
    compatibility: null,          // real Compatibility engine ships later — no fake %
    matchReasons: [],             // no fake breakdown either
    reputation: 0,
    postedAt: fmtRelative(row.created_at),
    postedAtMinutes: Math.floor((Date.now() - createdMs) / 60000),
    tags: row.tags || [],
    urgent: !!row.urgent,
    isNew: Date.now() - createdMs < 24 * 3600 * 1000,
    ownerId: row.owner_id,
    status: row.status,
  };
}

/* ────────── PROFILES (people & workshops) ────────── */
/** Directory listing — reads the safe view so private profiles stay discoverable
 *  by capability but keep their identity/detail columns null unless open. */
export async function listProducers() {
  const { data, error } = await getActiveClient()
    .from("profiles_directory").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(mapProfile);
}

/** Fetch a single producer. Tries the base profiles table first (returns full
 *  data when the profile is open or the viewer is the owner/admin); falls back
 *  to the directory view for the safe preview when access is not granted. */
export async function getProducer(id) {
  const c = getActiveClient();
  const { data: full } = await c.from("profiles").select("*").eq("id", id).maybeSingle();
  if (full) return mapProfile(full);
  const { data: preview } = await c.from("profiles_directory").select("*").eq("id", id).maybeSingle();
  return mapProfile(preview);
}

export async function getProducersByIds(ids) {
  if (!ids?.length) return [];
  const { data } = await getActiveClient().from("profiles_directory").select("*").in("id", ids);
  return (data || []).map(mapProfile);
}

/* ────────── REQUESTS ────────── */
export async function listRequests({ limit = 100, excludeOwner = null } = {}) {
  let q = getActiveClient()
    .from("requests")
    .select("*")
    .eq("status", "open")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (excludeOwner) q = q.neq("owner_id", excludeOwner);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map((r) => mapRequest(r, ""));
}

export async function myRequests(uid) {
  if (!uid) return [];
  const { data } = await getActiveClient()
    .from("requests").select("*").eq("owner_id", uid).order("created_at", { ascending: false });
  return (data || []).map((r) => mapRequest(r, "You"));
}

/** Top requests ranked by real activity: proposals count desc, then active negotiations desc,
 *  then created_at desc. Falls back to "latest active" ordering when there is no activity yet. */
export async function topRequestsRanked({ limit = 3, excludeOwner = null } = {}) {
  const c = getActiveClient();
  let q = c.from("requests").select("*").eq("status", "open");
  if (excludeOwner) q = q.neq("owner_id", excludeOwner);
  const { data: reqs, error } = await q;
  if (error) { console.error("[topRequestsRanked] failed", error); return []; }
  if (!reqs?.length) return [];
  const ids = reqs.map((r) => r.id);
  const { data: apps } = await c.from("applications").select("request_id, status").in("request_id", ids);
  const counts = {};
  const negotiating = {};
  (apps || []).forEach((a) => {
    counts[a.request_id] = (counts[a.request_id] || 0) + 1;
    if (["negotiating","viewed","sent","pending"].includes(a.status)) {
      negotiating[a.request_id] = (negotiating[a.request_id] || 0) + 1;
    }
  });
  const ranked = reqs
    .map((r) => ({ r, c: counts[r.id] || 0, n: negotiating[r.id] || 0 }))
    .sort((a, b) => {
      if (b.c !== a.c) return b.c - a.c;
      if (b.n !== a.n) return b.n - a.n;
      return new Date(b.r.created_at) - new Date(a.r.created_at);
    })
    .slice(0, limit);
  return ranked.map(({ r, c: pCount, n: neg }) => ({
    ...mapRequest(r, ""),
    proposalsCount: pCount,
    activeNegotiations: neg,
  }));
}

/** myRequests + a counts summary per request. */
export async function myRequestsWithCounts(uid) {
  if (!uid) return [];
  const c = getActiveClient();
  const { data: reqs } = await c.from("requests").select("*").eq("owner_id", uid).order("created_at", { ascending: false });
  if (!reqs?.length) return [];
  const ids = reqs.map((r) => r.id);
  const { data: apps } = await c.from("applications").select("request_id, status").in("request_id", ids);
  const counts = {};
  (apps || []).forEach((a) => {
    counts[a.request_id] = counts[a.request_id] || { total: 0, pending: 0, accepted: 0, declined: 0 };
    counts[a.request_id].total += 1;
    if (["pending","sent","viewed","negotiating"].includes(a.status)) counts[a.request_id].pending += 1;
    if (a.status === "accepted") counts[a.request_id].accepted += 1;
    if (a.status === "declined") counts[a.request_id].declined += 1;
  });
  return reqs.map((r) => ({ ...mapRequest(r, "You"), counts: counts[r.id] || { total: 0, pending: 0, accepted: 0, declined: 0 } }));
}

export async function createRequest(uid, form, files = []) {
  const client = getActiveClient();
  const refCode = `REQ-${Math.floor(Math.random() * 9000 + 1000)}`;
  const payload = {
    ref_code: refCode,
    owner_id: uid,
    title: form.title || "Untitled request",
    description: form.description || null,
    category: form.category || null,
    material: form.material || null,
    quantity: form.quantity ? Number(form.quantity) : null,
    budget_min: form.budgetMin ?? null,
    budget_max: form.budgetMax ?? null,
    deadline: form.deadline || null,
    location: form.location || null,
    tags: form.tags || [],
    required_processes: form.requiredProcesses || [],
    required_materials: form.requiredMaterials || [],
    urgent: !!form.urgent,
    status: "open",
  };
  const { data, error } = await client.from("requests").insert(payload).select().single();
  if (error) throw error;
  for (const file of files) {
    try {
      await uploadAttachment(uid, data.id, file);
    } catch (e) {
      console.error("attachment upload failed", e);
    }
  }
  return { row: data, refCode };
}

export async function uploadAttachment(uid, requestId, file) {
  const client = getActiveClient();
  const path = `${uid}/${requestId}/${Date.now()}-${file.name}`;
  const { error: upErr } = await client.storage.from("r4pro").upload(path, file, { upsert: false });
  if (upErr) throw upErr;
  await client.from("attachments").insert({
    owner_id: uid, request_id: requestId, path,
    filename: file.name, mime_type: file.type, size_bytes: file.size,
  });
  return path;
}

/* ────────── APPLICATIONS ────────── */
export async function myReplies(uid) {
  if (!uid) return [];
  const c = getActiveClient();
  const { data: reqs } = await c.from("requests").select("id, ref_code").eq("owner_id", uid);
  if (!reqs?.length) return [];
  const reqIds = reqs.map((r) => r.id);
  const { data, error } = await c
    .from("applications")
    .select("*")
    .in("request_id", reqIds)
    .order("created_at", { ascending: false });
  if (error) { console.error("[myReplies] failed", error); return []; }
  const rows = data || [];
  const applicantIds = [...new Set(rows.map((a) => a.applicant_id).filter(Boolean))];
  let profilesById = {};
  if (applicantIds.length) {
    const { data: profs } = await c
      .from("profiles")
      .select("id, full_name, handle, reputation, account_type")
      .in("id", applicantIds);
    (profs || []).forEach((p) => { profilesById[p.id] = p; });
  }
  return rows.map((a) => {
    const req = reqs.find((r) => r.id === a.request_id);
    const p = profilesById[a.applicant_id] || {};
    return {
      id: a.id,
      onRequest: req?.ref_code || a.request_id,
      requestId: a.request_id,
      from: p.full_name || p.handle || "—",
      applicantId: a.applicant_id,
      reputation: p.reputation || 0,
      userType: p.account_type === "PERSON" ? "person" : "workshop",
      excerpt: a.excerpt || "",
      excerptEn: a.excerpt || "",
      proposedPrice: a.proposed_price,
      proposedDays: a.proposed_days,
      status: a.status,
      when: fmtRelative(a.created_at),
      unread: a.status === "pending",
    };
  });
}

/** Fetch a request by its human ref_code (REQ-XXXX) or uuid. Returns the mapped shape plus dbId. */
export async function getRequestByRef(refOrId) {
  if (!refOrId) return null;
  const c = getActiveClient();
  // Try ref_code first (fast path — indexed unique)
  let { data } = await c.from("requests").select("*").eq("ref_code", refOrId).maybeSingle();
  if (!data) {
    // Fallback: uuid
    const attempt = await c.from("requests").select("*").eq("id", refOrId).maybeSingle();
    data = attempt.data;
  }
  if (!data) return null;
  // Enrich with owner display name
  let ownerName = "";
  if (data.owner_id) {
    const { data: owner } = await c.from("profiles").select("full_name, handle, account_type").eq("id", data.owner_id).maybeSingle();
    ownerName = owner?.full_name || owner?.handle || "";
  }
  return mapRequest(data, ownerName);
}

export async function getRequestAttachments(requestId) {
  if (!requestId) return [];
  const { data } = await getActiveClient()
    .from("attachments").select("*").eq("request_id", requestId).order("created_at", { ascending: true });
  return (data || []).map((a) => ({
    id: a.id,
    filename: a.filename || a.path.split("/").pop(),
    mimeType: a.mime_type || "",
    sizeBytes: a.size_bytes || 0,
    path: a.path,
  }));
}

/** Signed URL for downloading an attachment (private bucket). */
export async function attachmentSignedUrl(path, seconds = 300) {
  const { data } = await getActiveClient().storage.from("r4pro").createSignedUrl(path, seconds);
  return data?.signedUrl || null;
}

/** Submit a proposal (application) on a request. Also opens a chat and seeds it with the message.
 *  If a proposal already exists for this (request, applicant), fall back to an UPDATE so the
 *  producer can resend without hitting the unique constraint. */
export async function createProposal(uid, requestDbId, form, files = []) {
  if (!uid) throw new Error("Sign in required");
  const c = getActiveClient();
  const { data: req, error: reqErr } = await c
    .from("requests").select("id, ref_code, owner_id, title, status").eq("id", requestDbId).maybeSingle();
  if (reqErr || !req) throw new Error("Request not found");
  if (req.owner_id === uid) throw new Error("You cannot reply to your own request");
  if (req.status !== "open") throw new Error("This request is no longer accepting proposals");

  const priceNum = form.price === "" || form.price == null ? null : Number(form.price);
  const daysNum  = form.days  === "" || form.days  == null ? null : Number(form.days);

  let app = null;
  let wasUpdate = false;
  const insertRes = await c.from("applications").insert({
    request_id: req.id,
    applicant_id: uid,
    excerpt: form.message || null,
    proposed_price: priceNum,
    proposed_days: daysNum,
    status: "pending",
  }).select().single();

  if (insertRes.error) {
    // 23505 = unique_violation → already applied. Fall through to update path.
    if (insertRes.error.code === "23505") {
      const existing = await myProposalOnRequest(uid, req.id);
      if (!existing) throw insertRes.error;
      app = await updateProposal(existing.id, { message: form.message, price: form.price, days: form.days });
      wasUpdate = true;
    } else {
      throw insertRes.error;
    }
  } else {
    app = insertRes.data;
  }

  for (const file of files) {
    try { await uploadAttachment(uid, req.id, file); } catch (e) { console.error("attach upload failed", e); }
  }

  let chatId = null;
  try {
    chatId = await createOrGetChat(uid, req.owner_id, req.id);
    if (chatId && form.message) {
      const prefix = `[${req.ref_code}] `;
      const banner = wasUpdate ? "Proposal updated — " : "";
      await sendMessage(chatId, uid, `${prefix}${banner}${form.message}`);
    }
  } catch (e) { console.error("chat create failed", e); }

  return { application: app, chatId, refCode: req.ref_code, requestDbId: req.id, wasUpdate };
}

/** Proposals I sent. */
export async function myProposals(uid) {
  if (!uid) return [];
  const { data } = await getActiveClient()
    .from("applications")
    .select("*, request:requests!applications_request_id_fkey(id, ref_code, title, owner_id, status)")
    .eq("applicant_id", uid)
    .order("created_at", { ascending: false });
  return (data || []).map((a) => ({
    id: a.id,
    status: a.status,
    excerpt: a.excerpt || "",
    proposedPrice: a.proposed_price,
    proposedDays: a.proposed_days,
    when: fmtRelative(a.created_at),
    request: a.request ? { id: a.request.id, refCode: a.request.ref_code, title: a.request.title, ownerId: a.request.owner_id, status: a.request.status } : null,
  }));
}

/** Proposals received on a request I own. */
export async function proposalsForRequest(requestDbId) {
  if (!requestDbId) return [];
  const c = getActiveClient();
  const { data, error } = await c
    .from("applications")
    .select("*")
    .eq("request_id", requestDbId)
    .order("created_at", { ascending: false });
  if (error) { console.error("[proposalsForRequest] failed", error); return []; }
  const rows = data || [];
  const ids = [...new Set(rows.map((a) => a.applicant_id).filter(Boolean))];
  let profilesById = {};
  if (ids.length) {
    const { data: profs } = await c
      .from("profiles")
      .select("id, full_name, handle, reputation, account_type, positioning")
      .in("id", ids);
    (profs || []).forEach((p) => { profilesById[p.id] = p; });
  }
  return rows.map((a) => {
    const p = profilesById[a.applicant_id] || null;
    return {
      id: a.id,
      status: a.status,
      excerpt: a.excerpt || "",
      proposedPrice: a.proposed_price,
      proposedDays: a.proposed_days,
      when: fmtRelative(a.created_at),
      applicant: p ? {
        id: p.id,
        name: p.full_name || p.handle || "—",
        accountType: p.account_type,
        reputation: p.reputation || 0,
        positioning: p.positioning || "",
      } : null,
    };
  });
}

/** Find the chat linking `uid` and (optionally) `otherUid` on a given request. */
export async function findChatForRequest(uid, requestDbId, otherUid = null) {
  if (!uid || !requestDbId) return null;
  const c = getActiveClient();
  const { data } = await c.from("chats").select("*")
    .eq("request_id", requestDbId)
    .or(`participant_a.eq.${uid},participant_b.eq.${uid}`);
  if (!data?.length) return null;
  if (otherUid) {
    const match = data.find((x) => x.participant_a === otherUid || x.participant_b === otherUid);
    return match?.id || null;
  }
  return data[0].id;
}

/** Update application status (accept / decline / withdraw). Returns the updated row; throws if RLS blocked. */
export async function updateProposalStatus(proposalId, status) {
  const { data, error } = await getActiveClient()
    .from("applications").update({ status }).eq("id", proposalId).select();
  if (error) throw error;
  if (!data || data.length === 0) throw new Error("Update did not affect any row (permission?)");
  return data[0];
}

/** Fetch a single proposal by id with applicant + request context.
 *  NOTE: We deliberately split the query into base-row + profile + request lookups because
 *  PostgREST cannot resolve `profiles!applications_applicant_id_fkey` — the FK on
 *  applications.applicant_id points to auth.users, not public.profiles, so an embed hint
 *  returns PGRST200 and silently produces `data: null`. */
export async function getProposal(proposalId) {
  if (!proposalId) return null;
  const c = getActiveClient();
  const { data, error } = await c
    .from("applications")
    .select("*")
    .eq("id", proposalId)
    .maybeSingle();
  if (error) { console.error("[getProposal] applications lookup failed", error); return null; }
  if (!data) return null;

  const [profileRes, requestRes] = await Promise.all([
    data.applicant_id
      ? c.from("profiles")
          .select("id, full_name, handle, reputation, account_type, positioning, location, availability")
          .eq("id", data.applicant_id).maybeSingle()
      : Promise.resolve({ data: null }),
    data.request_id
      ? c.from("requests")
          .select("id, ref_code, title, owner_id, status, material, quantity, budget_min, budget_max, deadline, location")
          .eq("id", data.request_id).maybeSingle()
      : Promise.resolve({ data: null }),
  ]);
  const applicant = profileRes.data || null;
  const request   = requestRes.data || null;

  return {
    id: data.id,
    status: data.status,
    excerpt: data.excerpt || "",
    proposedPrice: data.proposed_price,
    proposedDays: data.proposed_days,
    createdAt: data.created_at,
    when: fmtRelative(data.created_at),
    applicant: applicant ? {
      id: applicant.id,
      name: applicant.full_name || applicant.handle || "—",
      accountType: applicant.account_type,
      reputation: applicant.reputation || 0,
      positioning: applicant.positioning || "",
      location: applicant.location || "",
      availability: applicant.availability || "available",
    } : null,
    request: request ? {
      id: request.id,
      refCode: request.ref_code,
      title: request.title,
      ownerId: request.owner_id,
      status: request.status,
      material: request.material,
      quantity: request.quantity,
      budget: fmtBudget(request.budget_min, request.budget_max),
      deadline: request.deadline,
      location: request.location,
    } : null,
  };
}

/** Owner-only lifecycle transitions for a request. Never deletes rows — we only
 *  change status so that history remains intact for stats/rankings. */
export async function updateRequestStatus(requestDbId, nextStatus) {
  if (!requestDbId) throw new Error("missing request id");
  const allowed = ["open", "closed", "completed", "archived", "draft"];
  if (!allowed.includes(nextStatus)) throw new Error("invalid status");
  const { data, error } = await getActiveClient()
    .from("requests").update({ status: nextStatus }).eq("id", requestDbId).select();
  if (error) throw error;
  if (!data?.length) throw new Error("Update did not affect any row (permission?)");
  return data[0];
}

export const completeRequest = (id) => updateRequestStatus(id, "completed");
export const archiveRequest  = (id) => updateRequestStatus(id, "archived");
export const reopenRequest   = (id) => updateRequestStatus(id, "open");

/** Look up MY existing proposal on a given request (if any). */
export async function myProposalOnRequest(uid, requestDbId) {
  if (!uid || !requestDbId) return null;
  const { data } = await getActiveClient()
    .from("applications").select("*")
    .eq("applicant_id", uid).eq("request_id", requestDbId).maybeSingle();
  return data || null;
}

/** Resolve a proposal id for a legacy proposal notification whose payload lacks proposal_id.
 *  For request owners → picks the latest proposal on the request.
 *  For applicants     → picks their own proposal on the request. */
export async function resolveProposalIdForNotif(uid, payload = {}) {
  if (payload?.proposal_id) return payload.proposal_id;
  const requestId = payload?.request_id;
  if (!uid || !requestId) return null;
  const c = getActiveClient();
  if (payload?.applicant_id && payload.applicant_id === uid) {
    const { data } = await c.from("applications").select("id")
      .eq("request_id", requestId).eq("applicant_id", uid).maybeSingle();
    return data?.id || null;
  }
  // Owner path — pick latest
  const { data } = await c.from("applications").select("id, applicant_id, created_at")
    .eq("request_id", requestId).order("created_at", { ascending: false }).limit(1);
  return data?.[0]?.id || null;
}

/** Update fields of an existing proposal (applicant only). Also drops a short notice into the
 *  chat so the customer sees the change in the same negotiation thread. */
export async function updateProposal(proposalId, patch) {
  const c = getActiveClient();
  const payload = {};
  if (patch.message !== undefined) payload.excerpt = patch.message;
  if (patch.price !== undefined) payload.proposed_price = patch.price === "" || patch.price == null ? null : Number(patch.price);
  if (patch.days !== undefined) payload.proposed_days = patch.days === "" || patch.days == null ? null : Number(patch.days);
  const { data, error } = await c
    .from("applications").update(payload).eq("id", proposalId).select();
  if (error) throw error;
  if (!data?.length) throw new Error("Update did not affect any row (permission?)");
  const updated = data[0];

  // Best-effort: post an inline chat notice so the customer sees the edit in context.
  try {
    const { data: prop } = await c
      .from("applications")
      .select("applicant_id, proposed_price, proposed_days, excerpt, request:requests!applications_request_id_fkey(id, ref_code, owner_id)")
      .eq("id", proposalId).maybeSingle();
    if (prop?.applicant_id && prop.request?.owner_id && prop.request?.id) {
      const chatId = await createOrGetChat(prop.applicant_id, prop.request.owner_id, prop.request.id);
      if (chatId) {
        const parts = [`[${prop.request.ref_code}]`, "Proposal updated —"];
        if (prop.proposed_price != null) parts.push(`price € ${Number(prop.proposed_price).toLocaleString("it-IT")}`);
        if (prop.proposed_days  != null) parts.push(`· delivery ${prop.proposed_days}d`);
        if (prop.excerpt) parts.push(`\n${prop.excerpt}`);
        await sendMessage(chatId, prop.applicant_id, parts.join(" "));
      }
    }
  } catch (e) { console.warn("update proposal chat notice failed", e); }

  return updated;
}

/** Owner accepts a proposal: sets status, closes the request, ensures a chat exists.
 *  Returns { chatId }. Never throws once the status flip has been persisted — chat creation
 *  failure is best-effort so the accepted state is always durable. */
export async function acceptProposal(uid, proposalId) {
  const c = getActiveClient();
  const { data: prop, error } = await c
    .from("applications").select("*, request:requests!applications_request_id_fkey(id, owner_id, ref_code)")
    .eq("id", proposalId).maybeSingle();
  if (error) throw error;
  if (!prop) throw new Error("Proposal not found");
  if (prop.request?.owner_id !== uid) throw new Error("Only the request owner can accept");

  await updateProposalStatus(proposalId, "accepted");
  const { error: reqErr } = await c.from("requests").update({ status: "closed" }).eq("id", prop.request.id);
  if (reqErr) console.warn("could not close request after accept", reqErr);
  let chatId = null;
  try {
    chatId = await createOrGetChat(uid, prop.applicant_id, prop.request.id);
  } catch (e) { console.warn("could not open chat after accept", e); }
  return { chatId, refCode: prop.request?.ref_code };
}

/** Owner declines a proposal: sets status; request remains open. */
export async function rejectProposal(uid, proposalId) {
  const c = getActiveClient();
  const { data: prop } = await c.from("applications")
    .select("id, request_id, request:requests!applications_request_id_fkey(owner_id)")
    .eq("id", proposalId).maybeSingle();
  if (!prop) throw new Error("Proposal not found");
  if (prop.request?.owner_id !== uid) throw new Error("Only the request owner can decline");
  await updateProposalStatus(proposalId, "declined");
}

/* ────────── CHATS & MESSAGES ────────── */
export async function listChats(uid) {
  if (!uid) return [];
  const c = getActiveClient();
  // NOTE: profiles cannot be embedded via participant_a/b FKs because those point to auth.users
  // (not public.profiles) — PostgREST returns PGRST200 for that embed. Fetch profiles separately.
  const { data, error } = await c
    .from("chats")
    .select("*, request:requests!chats_request_id_fkey(id, ref_code, title, status)")
    .or(`participant_a.eq.${uid},participant_b.eq.${uid}`)
    .order("created_at", { ascending: false });
  if (error) { console.error("[listChats] failed", error); return []; }
  const rows = data || [];
  const otherIds = [...new Set(
    rows.map((x) => (x.participant_a === uid ? x.participant_b : x.participant_a)).filter(Boolean)
  )];
  let byId = {};
  if (otherIds.length) {
    const { data: profs } = await c.from("profiles").select("id, full_name, handle").in("id", otherIds);
    (profs || []).forEach((p) => { byId[p.id] = p; });
  }
  return rows.map((chat) => {
    const otherId = chat.participant_a === uid ? chat.participant_b : chat.participant_a;
    const other = byId[otherId] || null;
    const name = other?.full_name || other?.handle || "—";
    return {
      id: chat.id,
      with: name,
      otherId,
      requestId: chat.request_id,
      request: chat.request ? { id: chat.request.id, refCode: chat.request.ref_code, title: chat.request.title, status: chat.request.status } : null,
      lastMessage: "",
      lastMessageEn: "",
      time: fmtRelative(chat.created_at),
      unread: 0,
      online: false,
      initials: name.split(" ").map((w) => w[0]).slice(0, 2).join("") || "?",
    };
  });
}

export async function getMessages(chatId) {
  const { data } = await getActiveClient()
    .from("messages").select("*").eq("chat_id", chatId).order("created_at", { ascending: true });
  return (data || []).map((m) => ({
    id: m.id,
    from: m.sender_id,
    text: m.content || "",
    textEn: m.content || "",
    time: fmtRelative(m.created_at),
    createdAt: m.created_at,
  }));
}

export async function sendMessage(chatId, senderId, content) {
  const { data } = await getActiveClient()
    .from("messages").insert({ chat_id: chatId, sender_id: senderId, content }).select().single();
  return data;
}

export function subscribeMessages(chatId, callback) {
  const client = getActiveClient();
  const channel = client
    .channel(`messages-${chatId}`)
    .on("postgres_changes",
      { event: "INSERT", schema: "public", table: "messages", filter: `chat_id=eq.${chatId}` },
      (payload) => callback(payload.new))
    .subscribe();
  return () => client.removeChannel(channel);
}

/** Find an existing chat between two users FOR A GIVEN request, or create one. Returns chat id. */
export async function createOrGetChat(uid, otherUid, requestId = null) {
  if (!uid || !otherUid || uid === otherUid) throw new Error("invalid chat participants");
  const c = getActiveClient();
  let query = c.from("chats").select("*").or(`participant_a.eq.${uid},participant_b.eq.${uid}`);
  if (requestId) query = query.eq("request_id", requestId);
  else query = query.is("request_id", null);
  const { data } = await query;
  const found = (data || []).find((x) =>
    (x.participant_a === uid && x.participant_b === otherUid) ||
    (x.participant_a === otherUid && x.participant_b === uid)
  );
  if (found) return found.id;
  const { data: created, error } = await c.from("chats")
    .insert({ participant_a: uid, participant_b: otherUid, request_id: requestId })
    .select().single();
  if (error) throw error;
  return created.id;
}

/** Persistently update the current user's profile visibility (open | private).
 *  Also flips profiles.visibility_explicit → true so any future re-run of the
 *  visibility backfill migration can never overturn the user's own choice. */
export async function updateVisibility(uid, visibility) {
  if (!uid) throw new Error("no user");
  if (!["open", "private"].includes(visibility)) throw new Error("invalid visibility");
  const { data, error } = await getActiveClient()
    .from("profiles")
    .update({ visibility, visibility_explicit: true })
    .eq("id", uid)
    .select("visibility, visibility_explicit")
    .single();
  if (error) throw error;
  return data?.visibility || visibility;
}

/** Create a profile-access request. Idempotent per (requester, profile) via unique key. */
export async function requestProfileAccess(requesterId, profileId) {
  if (!requesterId || !profileId) throw new Error("missing ids");
  if (requesterId === profileId) throw new Error("cannot request your own profile");
  const c = getActiveClient();
  const { data, error } = await c
    .from("profile_access_requests")
    .upsert(
      { requester_id: requesterId, profile_id: profileId, status: "pending" },
      { onConflict: "requester_id,profile_id", ignoreDuplicates: false }
    )
    .select("id, status")
    .single();
  if (error) throw error;
  return data;
}

/** Read the requester's own status for a given target profile (null if never requested). */
export async function getMyAccessRequest(requesterId, profileId) {
  if (!requesterId || !profileId) return null;
  const { data } = await getActiveClient()
    .from("profile_access_requests")
    .select("id, status, created_at, responded_at")
    .eq("requester_id", requesterId).eq("profile_id", profileId).maybeSingle();
  return data || null;
}

/** Update the current user's availability. */
export async function updateAvailability(uid, status) {
  if (!uid) throw new Error("no user");
  const { error } = await getActiveClient()
    .from("profiles").update({ availability: status }).eq("id", uid);
  if (error) throw error;
}

/** Patch the current user's profile row.
 *  Uses .select().single() so silent 0-row updates (RLS mismatch, schema-cache misses,
 *  missing profile row) surface as real errors instead of pretending to succeed.
 *  Returns the freshly updated row. */
export async function updateProfile(uid, patch) {
  if (!uid) throw new Error("no user");
  const { data, error } = await getActiveClient()
    .from("profiles")
    .update(patch)
    .eq("id", uid)
    .select("*")
    .single();
  if (error) throw error;
  if (!data) throw new Error("Profile update affected 0 rows (check permissions).");
  return data;
}

/** Simple network counters for Dashboard. */
export async function getNetworkStats() {
  const c = getActiveClient();
  const [prodTotal, openReq, people, orgs, completed] = await Promise.all([
    c.from("profiles").select("*", { count: "exact", head: true }),
    c.from("requests").select("*", { count: "exact", head: true }).eq("status", "open"),
    c.from("profiles").select("*", { count: "exact", head: true }).eq("account_type", "PERSON"),
    c.from("profiles").select("*", { count: "exact", head: true }).eq("account_type", "ORGANIZATION"),
    c.from("applications").select("*", { count: "exact", head: true }).eq("status", "completed"),
  ]);
  return {
    producers: prodTotal.count || 0,
    openRequests: openReq.count || 0,
    people: people.count || 0,
    organizations: orgs.count || 0,
    completedCollaborations: completed.count || 0,
  };
}

/** Real sidebar/nav counters — per-user unread state (persistent in Supabase).
 *  - newOpportunities: open requests I did NOT create, created in the last 7 days
 *  - newProfiles:      profiles (other than mine) I have NOT viewed yet (persistent)
 *  - newMessages:      count of messages sent to me (in chats I participate in) that
 *                      arrived after my per-chat last_read timestamp.
 *  - notifications:    unread notifications (from countUnreadNotifications) */
export async function getNavCounters(uid) {
  const c = getActiveClient();
  const sinceIso = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

  // newOpportunities — unchanged
  const opps = c.from("requests").select("*", { count: "exact", head: true })
    .eq("status", "open").gte("created_at", sinceIso);
  if (uid) opps.neq("owner_id", uid);
  const oR = await opps;

  // newProfiles — count profiles I have not yet viewed.
  let newProfiles = 0;
  if (uid) {
    const { data: viewed } = await c.from("profile_views")
      .select("profile_id").eq("viewer_id", uid);
    const viewedIds = (viewed || []).map((v) => v.profile_id);
    let pq = c.from("profiles").select("*", { count: "exact", head: true }).neq("id", uid);
    if (viewedIds.length) {
      pq = pq.not("id", "in", `(${viewedIds.join(",")})`);
    }
    const pR = await pq;
    newProfiles = pR.count || 0;
  }

  // newMessages — count of incoming messages in my chats that are newer than
  // my per-participant last_read timestamp on that chat.
  let newMessages = 0;
  if (uid) {
    const { data: myChats } = await c.from("chats")
      .select("id, participant_a, participant_b, last_read_a, last_read_b")
      .or(`participant_a.eq.${uid},participant_b.eq.${uid}`);
    const chats = myChats || [];
    if (chats.length) {
      const results = await Promise.all(chats.map(async (chat) => {
        const myLastRead = chat.participant_a === uid ? chat.last_read_a : chat.last_read_b;
        let q = c.from("messages").select("*", { count: "exact", head: true })
          .eq("chat_id", chat.id).neq("sender_id", uid);
        if (myLastRead) q = q.gt("created_at", myLastRead);
        const { count } = await q;
        return count || 0;
      }));
      newMessages = results.reduce((a, b) => a + b, 0);
    }
  }

  return {
    newOpportunities: oR.count || 0,
    newProfiles,
    newMessages,
  };
}

/** Persistently mark a profile as viewed by the current user. Only affects THIS user. */
export async function markProfileViewed(viewerId, profileId) {
  if (!viewerId || !profileId || viewerId === profileId) return;
  const { error } = await getActiveClient()
    .from("profile_views")
    .upsert(
      { viewer_id: viewerId, profile_id: profileId, viewed_at: new Date().toISOString() },
      { onConflict: "viewer_id,profile_id" }
    );
  if (error) console.warn("[markProfileViewed]", error);
}

/** Persistently mark a chat as read by the current user (updates the correct
 *  last_read_a / last_read_b column depending on which participant the user is). */
export async function markChatRead(chatId, uid) {
  if (!chatId || !uid) return;
  const c = getActiveClient();
  const { data: chat } = await c.from("chats")
    .select("participant_a, participant_b").eq("id", chatId).maybeSingle();
  if (!chat) return;
  const nowIso = new Date().toISOString();
  const patch = chat.participant_a === uid
    ? { last_read_a: nowIso }
    : chat.participant_b === uid
      ? { last_read_b: nowIso }
      : null;
  if (!patch) return;
  const { error } = await c.from("chats").update(patch).eq("id", chatId);
  if (error) console.warn("[markChatRead]", error);
}

/* ────────── NOTIFICATIONS ────────── */
export async function listNotifications(uid) {
  if (!uid) return [];
  const { data } = await getActiveClient()
    .from("notifications").select("*").eq("user_id", uid).order("created_at", { ascending: false });
  return (data || []).map((n) => ({
    id: n.id,
    type: n.kind || "system",
    title: n.title || "",
    titleEn: n.title || "",
    time: fmtRelative(n.created_at),
    unread: !n.is_read,
    resolved: n.payload?.resolved || null,
    payload: n.payload || {},
  }));
}

export async function markAllNotificationsRead(uid) {
  await getActiveClient().from("notifications").update({ is_read: true }).eq("user_id", uid);
}

/** Mark ONE notification as read. Used when the user actually opens/views it —
 *  we never mark a batch automatically. */
export async function markNotificationRead(id) {
  if (!id) return;
  await getActiveClient().from("notifications").update({ is_read: true }).eq("id", id);
}

export async function resolveNotification(id, action) {
  await getActiveClient().from("notifications")
    .update({ is_read: true, payload: { resolved: action } }).eq("id", id);
}

/** Live subscription to a user's notifications (INSERT + UPDATE). */
export function subscribeNotifications(uid, callback) {
  if (!uid) return () => {};
  const client = getActiveClient();
  const channel = client
    .channel(`notifs-${uid}`)
    .on("postgres_changes",
      { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${uid}` },
      (payload) => callback(payload))
    .subscribe();
  return () => client.removeChannel(channel);
}

/** Fast unread-notifications count. */
export async function countUnreadNotifications(uid) {
  if (!uid) return 0;
  const { count } = await getActiveClient()
    .from("notifications").select("*", { count: "exact", head: true })
    .eq("user_id", uid).eq("is_read", false);
  return count || 0;
}
